export const ordersArticles = [
    {
        id: 'orders.overview',
        title: 'Orders Overview',
        category: 'Orders',
        order: 1,
        summary: 'Managing purchase orders and supplier commitments',
        content: `
The Orders page is where you create, manage, and track purchase orders for your jobs. Orders convert BOQ items into formal commitments with suppliers.

**Order lifecycle:**

1. **Draft** — raised from the BOQ (a job, cost centre and load), being prepared
2. **Ordered** — sent: the lines are locked, the order is committed against the job, the PO is archived and (if you chose) emailed
3. **Completed** — marked complete once delivered and invoiced
4. **Cancelled** — released: the lines are free to order again

Order numbers are composed by Costed as *job/cost-centre* for load 1 and *job/cost-centre.n* for other loads — you cannot type one.

**Who can do what:** anyone except a Viewer can raise, edit and send orders. Cancelling, reinstating, marking fully invoiced and importing need the owner, admin or manager role. If **Orders require authorisation** is on in Settings → Accounting, people below those roles can prepare an order but not send it; a money role sends it (that is the authorisation). Per-user order limits (when switched on) still apply.

**Key Features:**

- Create orders from BOQ items
- Track order status
- Generate order PDFs
- Email orders to suppliers
- Match invoices to orders
    `,
        expandableSections: [
            {
                title: 'Order Grid',
                content: `
The grid shows all orders with key information:

- **Order Number** - Unique identifier
- **Job** - Associated job
- **Supplier** - Who the order is with
- **Status** - Current state
- **Total** - Order value
- **Sent Date** - When order was sent

**Filtering:**
- Use the filter row to search
- Filter by status, job, or supplier
- Apply saved views for common filters
        `,
            },
            {
                title: 'KPI Summary',
                content: `
The toolbar shows order KPIs:

- **Draft** - Orders not yet sent
- **Ordered** - Sent to the supplier
- **To Invoice** - Sent orders awaiting invoicing
- **Completed** - Finished orders
        `,
            },
        ],
        keywords: ['orders', 'purchase orders', 'PO', 'procurement'],
        route: '/orders',
        relatedArticles: ['orders.create', 'orders.send', 'suppliers.overview'],
    },
    {
        id: 'orders.create',
        title: 'Creating Orders',
        category: 'Orders',
        order: 2,
        summary: 'How to create purchase orders from BOQ items',
        content: `
Orders are created from BOQ items that have suppliers assigned. You can create orders for single or multiple items at once.

**Prerequisites:**

- Items must have a supplier assigned
- Items must not already be ordered
- Job must be active
    `,
        expandableSections: [
            {
                title: 'Creating an Order',
                content: `
**From Orders Page (New Draft Order):**
1. Select the Job, then click **New Order**
2. Pick the Cost Centre (and optionally the Supplier)
3. The **Load** number defaults to the next available load for that cost centre — override it if you need a specific load
4. Search the catalogue under **Items** (type any words, in any order) and click results to add them; adjust quantities and prices in the list
5. Click **Create Draft Order** — the picked items are added to the job's BOQ on that load automatically, and the order shows them immediately

**From BOQ Page:**
1. Select items with same supplier
2. Right-click > Create Order
3. Review and confirm

**Bulk Orders:**
1. Select multiple items (same job)
2. System groups by supplier
3. Creates one order per supplier
        `,
            },
            {
                title: 'Order Details',
                content: `
When creating an order, you can set:

- **Order Date** - Defaults to today
- **Required Date** - When items are needed
- **Delivery Address** - Job site or other
- **Reference** - Your reference number
- **Notes** - Instructions for supplier

These details appear on the order PDF.
        `,
            },
            {
                title: 'Adding Items from the Catalogue',
                content: `
The New Draft Order dialog has an **Items** search so you can build the order and the BOQ in one step:

1. Pick the cost centre first — the search is scoped to its catalogue items (tick **All cost centres** to search everything)
2. Type a few words in any order (e.g. "concrete 32") and click a result to add it
3. Adjust the quantity and unit price on each line; remove lines with the ✕
4. On **Create Draft Order**, each line is written to the job's **BOQ on the order's load** — the BOQ updates automatically and the order shows the items immediately

**Loads:** the Load number defaults to the **next available load** for that cost centre, so a fresh order never collides with existing loads. Loads already ordered are blocked (items on a sent order are locked).
        `,
            },
            {
                title: 'Standard Notes (canned comments)',
                content: `
Instead of re-typing the same order comments — payment terms, site conditions, compliance wording — keep them as **standard notes** and insert them with one click:

1. Set up the wording once in **Settings → Estimating Setup → Standard Notes** (add, edit, reorder; hide a note from the picker without deleting it)
2. Above the Notes box, click **Insert standard note…** and type a few words — in any order — to filter the list (e.g. "terms payment" finds "Payment terms: 30 days"). Pick a note (click, or arrow keys + Enter) and it's appended, so you can stack several and still type freely
3. The same picker is available when **editing an existing order** (double-click the order, or right-click → Edit Order)
4. The notes print on the purchase order PDF sent to the supplier

Coming from Databuild? This is the StandardNotes workflow — your existing notes can be imported during migration.
        `,
            },
            {
                title: 'Item Locking',
                content: `
Once items are on an order:

- **BOQ items are locked** - Cannot be deleted or modified
- **Prices are fixed** - Price at time of order
- **Supplier is locked** - Cannot change supplier

To unlock, the order must be cancelled.
        `,
            },
        ],
        keywords: ['create order', 'new order', 'purchase order', 'place order', 'standard notes', 'canned comments', 'order notes', 'add items', 'catalogue items', 'load number', 'next available load'],
        route: '/orders',
        relatedArticles: ['orders.overview', 'boq.suppliers'],
        screenshots: [
            {
                src: 'orders/orders-create.png',
                alt: 'New Draft Order dialog',
                caption: 'Create a draft order by picking the job, cost centre and load',
            },
        ],
    },
    {
        id: 'orders.edit',
        title: 'Editing Orders',
        category: 'Orders',
        order: 3,
        summary: 'How to modify order details and line items',
        content: `
You can edit orders while they are in Draft status. Once placed, editing is limited to preserve the record.

**Editable While Draft:**

- Add or remove items
- Change quantities
- Modify prices
- Update delivery details
- Change dates

**Editable After Placement:**

- Notes (for internal use)
- Status updates
- Delivery tracking
    `,
        expandableSections: [
            {
                title: 'Editing Process',
                content: `
1. Double-click the order in the grid
2. Or right-click > Edit Order
3. Make your changes:
   - Update header information
   - Modify line items
   - Adjust quantities or prices
4. Click **Save**

**Adding Items to Draft:**
1. Open the order for editing
2. Click **Add Items**
3. Select from available BOQ items
4. Confirm

**Inserting a Standard Note:**
1. Open the order for editing
2. Above the Notes box, click **Insert standard note…**
3. Type a few words (any order) to search, then pick the note — it's appended to the existing notes
        `,
            },
            {
                title: 'Revising Sent Orders',
                content: `
A sent (ordered) order cannot be edited. If it needs changes:

1. Cancel the original order with a reason (owner, admin or manager) — refused if an invoice is already matched to it
2. Its lines are released
3. Raise a new order on the same load with the corrections and send it

Or contact the supplier about amendments (tracked manually).
        `,
            },
        ],
        keywords: ['edit order', 'modify order', 'change order', 'update order', 'standard notes', 'order notes'],
        route: '/orders',
        relatedArticles: ['orders.overview', 'orders.create'],
    },
    {
        id: 'orders.send',
        title: 'Sending Orders',
        category: 'Orders',
        order: 4,
        summary: 'How to send orders to suppliers via email or download',
        content: `
Once an order is ready, you can send it to the supplier via email or download the PDF for manual delivery.

**Send Options:**

- **Email** - Send directly from the system
- **Download PDF** - Save for manual sending
- **Print** - Print physical copy
    `,
        expandableSections: [
            {
                title: 'Sending by Email',
                content: `
1. Select the order and click **Mark Sent** (or right-click → **Send / Email Order…**)
2. The send dialog opens with **Email the purchase order** switched on when the supplier has an email on file:
   - **To** — pre-filled from the supplier (editable)
   - **CC**, **Subject** and **Message** — pre-filled, edit freely
   - **Include transmittal** — optionally pick one of the job's transmittals: its cover sheet is attached and a secure link to view/download the documents is added to the email
3. Click **Send & Email** — the PO PDF is attached automatically

Before anything is written, Costed checks that the order has a supplier and at least one line, and — when emailing — that there is an address to send to; if not, it says so and nothing changes. Then the order is marked **ordered**, its lines are locked and it is committed against the job in one step, the PO PDF is archived to the job's documents (the same PDF the supplier receives) and the email goes out with **Reply-To** set to you. Every email is recorded on the order (when, to whom, by whom). If the email itself fails after the send, the order stays sent and you see the reason so you can email it again.

A sent order cannot be sent twice — use **Email Order…** to send the PO again.

Turn the email switch off to mark the order sent without emailing (e.g. when you've already sent it outside Costed). **Bulk send** (multiple orders selected) marks them sent without emailing.

**Re-sending a placed order:** right-click the order → **Email Order…** to email the PO PDF again (say the supplier misplaced it). The order's status and commitments are untouched, and the send is recorded in the **Email Log** report like any other.
        `,
            },
            {
                title: 'Download PDF',
                content: `
1. Select the order
2. Click **Preview** to view the PDF
3. Click **Download** to save locally
4. Send manually via your email client

Remember to update the status manually if sent externally.
        `,
            },
            {
                title: 'Order PDF Contents',
                content: `
The generated PDF includes:

- Company letterhead
- Order number and date
- Supplier details
- Job/delivery information
- Line items with descriptions
- Quantities and prices
- Subtotal (ex tax), tax at your workspace rate and name, total (inc tax)
- Terms and conditions
        `,
            },
        ],
        keywords: ['send order', 'email order', 'PDF', 'download', 'place order'],
        route: '/orders',
        relatedArticles: ['orders.overview', 'orders.create'],
    },
    {
        id: 'orders.cancel',
        title: 'Cancelling Orders',
        category: 'Orders',
        order: 5,
        summary: 'How to cancel orders and release locked items',
        content: `
Cancelling an order releases its BOQ lines (the load can be ordered again) and cancels its ledger commitment. It is refused when the order is already cancelled, marked fully invoiced, or has an invoice matched to it — resolve those first. The reason and who cancelled are kept on the order.

**When to Cancel:**

- Order placed in error
- Supplier cannot fulfill
- Project requirements changed
- Duplicate order
    `,
        expandableSections: [
            {
                title: 'Cancellation Process',
                content: `
1. Select the order to cancel
2. Click **Cancel Order** or right-click > Cancel
3. Confirm the cancellation
4. Provide a reason (optional but recommended)
5. The order status changes to "Cancelled"

**Effects:**
- BOQ items are unlocked
- Items available for new orders
- Order remains in history (as cancelled)
- Financial records updated
        `,
            },
            {
                title: 'Partial Cancellation',
                content: `
To cancel only some items from an order:

1. This requires cancelling the entire order
2. Re-create a new order without unwanted items

Note: Partially invoiced orders may require credit note handling.
        `,
            },
        ],
        keywords: ['cancel order', 'delete order', 'void order'],
        route: '/orders',
        relatedArticles: ['orders.overview', 'orders.edit'],
    },
    {
        id: 'orders.status',
        title: 'Order Status Workflow',
        category: 'Orders',
        order: 6,
        summary: 'Understanding order statuses and transitions',
        content: `
Orders progress through defined statuses that track their lifecycle from creation to completion.

**Status flow:**

\`\`\`
Draft → Ordered → Completed
  ↓        ↓
Cancelled  Cancelled  (reinstate → Draft)
\`\`\`
    `,
        expandableSections: [
            {
                title: 'Status Definitions',
                content: `
| Status | Description |
|--------|-------------|
| **Draft** | Being prepared. Lines can be added and edited; the order can be deleted. |
| **Ordered** | Sent to the supplier. Lines locked, commitment raised, PO archived. Only Email Order…, Cancel and Complete are available. |
| **Completed** | Delivered and invoiced. |
| **Cancelled** | Released. Lines free, commitment cancelled, reason kept. Can be reinstated to Draft. |

**Order Reconciliation report** (Reports → Procurement) checks every ordered purchase order against its BOQ lines: lines not flagged as ordered (common for orders brought across from Databuild), orders with only the Databuild snapshot and no live lines, no lines at all, or no supplier. An owner, admin, manager or accounts member can press **Flag** on a row, or **Flag all**, to mark the lines as ordered — exactly what Send does — so ordered and committed figures line up. **Purchase orders can also be sent to Xero or QuickBooks** when an order is placed (Settings → Accounting → Posting, with the Purchase Orders sync type ticked on the connection); see the Accounting → Posting help.
        `,
            },
            {
                title: 'Status Transitions',
                content: `
**Draft → Ordered:** only by sending (Mark Sent). Status can never be typed in — editing an order changes its supplier, dates and notes, nothing else.

**Ordered → Completed:** Mark Complete from the job's Purchase Orders panel once delivered and invoiced. A draft cannot be completed.

**Draft / Ordered → Cancelled:** Cancel with a reason (owner, admin or manager). Refused while an invoice is matched to the order or it is marked fully invoiced.

**Cancelled → Draft:** Reinstate (owner, admin or manager). The lines are locked again only when it is sent again.
        `,
            },
        ],
        keywords: ['status', 'workflow', 'lifecycle', 'draft', 'ordered', 'completed', 'cancelled', 'reinstate'],
        route: '/orders',
        relatedArticles: ['orders.overview', 'orders.send'],
    },
];
